import { useState, useEffect } from "react";
import { ScheduleItem } from "../types";

interface Props {
  item: ScheduleItem;
  onClose: () => void;
  onSave: (item: ScheduleItem, newScheduledAt: string) => void;
}

function toDateInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toTimeInput(date: Date): string {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export default function EditTimeModal({ item, onClose, onSave }: Props) {
  const [dateValue, setDateValue] = useState("");
  const [timeValue, setTimeValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    // Parse existing scheduled time or set default to 1 hour from now
    const parts = item.scheduledAt?.match(/(\d{4})\.(\d{2})\.(\d{2})\s(\d{2}):(\d{2})/);
    if (parts) {
      setDateValue(`${parts[1]}-${parts[2]}-${parts[3]}`);
      setTimeValue(`${parts[4]}:${parts[5]}`);
    } else {
      const inOneHour = new Date(Date.now() + 60 * 60 * 1000);
      setDateValue(toDateInput(inOneHour));
      setTimeValue(toTimeInput(inOneHour));
    }
  }, [item]);

  const handleSave = () => {
    if (!dateValue || !timeValue) {
      setError("날짜와 시간을 모두 입력해주세요.");
      return;
    }
    const now = new Date();
    const selected = new Date(`${dateValue}T${timeValue}:00`);
    if (selected <= now) {
      setError("현재 시각 이후로 설정해주세요.");
      return;
    }
    const [y, m, d] = dateValue.split("-");
    const [hh, mm] = timeValue.split(":");
    const formatted = `${y}.${m}.${d} ${hh}:${mm}`;
    onSave(item, formatted);
  };

  const getMinDate = () => toDateInput(new Date());
  const getMinTime = () => {
    const dateStr = toDateInput(new Date());
    if (dateValue === dateStr) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes() + 1).padStart(2, "0");
      return `${hh}:${mm}`;
    }
    return "00:00";
  };

  const presets = [
    { label: "1시간 후", offset: 60 },
    { label: "3시간 후", offset: 180 },
    { label: "내일 오전 9시", special: "tomorrow-9" },
    { label: "내일 오후 2시", special: "tomorrow-14" },
  ];

  const applyPreset = (preset: { label: string; offset?: number; special?: string }) => {
    const now = new Date();
    let target: Date;
    if (preset.offset) {
      target = new Date(now.getTime() + preset.offset * 60 * 1000);
    } else if (preset.special === "tomorrow-9") {
      target = new Date(now);
      target.setDate(target.getDate() + 1);
      target.setHours(9, 0, 0, 0);
    } else {
      target = new Date(now);
      target.setDate(target.getDate() + 1);
      target.setHours(14, 0, 0, 0);
    }
    setDateValue(toDateInput(target));
    setTimeValue(toTimeInput(target));
    setError("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#13131f] border border-white/[0.1] rounded-t-2xl md:rounded-2xl w-full md:max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-white/[0.07]">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg bg-violet-500/20">
              <i className="ri-time-line text-violet-400 text-xs md:text-sm"></i>
            </div>
            <div>
              <h3 className="text-white text-xs md:text-sm font-semibold">발송 시간 수정</h3>
              <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">새로운 예약 발송 시각을 설정하세요</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xs md:text-sm"></i>
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-4 md:space-y-5">
          {/* 현재 예약 시각 */}
          <div className="flex items-center gap-2 md:gap-3 px-3 py-2.5 md:px-4 md:py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
              <i className="ri-calendar-check-line text-gray-500 text-xs md:text-sm"></i>
            </div>
            <div>
              <p className="text-gray-500 text-[10px] md:text-xs">현재 예약 시각</p>
              <p className="text-gray-300 text-xs md:text-sm font-medium">{item.scheduledAt || "미정"}</p>
            </div>
          </div>

          {/* 빠른 선택 */}
          <div>
            <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">빠른 선택</p>
            <div className="grid grid-cols-4 md:grid-cols-2 gap-1.5 md:gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => applyPreset(preset)}
                  className="px-2.5 py-2 md:px-3 md:py-2 rounded-lg bg-white/[0.04] border border-white/[0.07] text-gray-400 text-[10px] md:text-xs hover:text-white hover:bg-white/[0.08] hover:border-violet-500/30 cursor-pointer transition-all whitespace-nowrap"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* 날짜 & 시간 입력 */}
          <div className="grid grid-cols-2 gap-2 md:gap-3">
            <div>
              <label className="block text-gray-400 text-[10px] md:text-xs font-medium mb-1 md:mb-1.5">날짜</label>
              <input
                type="date"
                value={dateValue}
                min={getMinDate()}
                onChange={(e) => { setDateValue(e.target.value); setError(""); }}
                className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-2.5 md:px-3 py-2 md:py-2.5 text-white text-xs md:text-sm focus:outline-none focus:border-violet-500/50 cursor-pointer"
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div>
              <label className="block text-gray-400 text-[10px] md:text-xs font-medium mb-1 md:mb-1.5">시간</label>
              <input
                type="time"
                value={timeValue}
                min={getMinTime()}
                onChange={(e) => { setTimeValue(e.target.value); setError(""); }}
                className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-2.5 md:px-3 py-2 md:py-2.5 text-white text-xs md:text-sm focus:outline-none focus:border-violet-500/50 cursor-pointer"
                style={{ colorScheme: "dark" }}
              />
            </div>
          </div>

          {/* 에러 */}
          {error && (
            <div className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-2 md:py-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="w-3.5 h-3.5 md:w-4 md:h-4 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-400 text-[10px] md:text-xs"></i>
              </div>
              <p className="text-red-400 text-[10px] md:text-xs">{error}</p>
            </div>
          )}

          {/* 미리보기 */}
          {dateValue && timeValue && !error && (
            <div className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-2 md:py-2.5 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <div className="w-3.5 h-3.5 md:w-4 md:h-4 flex items-center justify-center">
                <i className="ri-calendar-schedule-line text-violet-400 text-[10px] md:text-xs"></i>
              </div>
              <p className="text-violet-300 text-[10px] md:text-xs">
                <span className="font-medium">{dateValue.replace(/-/g, ".")} {timeValue}</span>으로 변경됩니다
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 md:gap-3 px-4 md:px-6 py-3 md:py-4 border-t border-white/[0.07]">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 md:px-4 md:py-2.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 text-xs md:text-sm hover:text-white hover:bg-white/[0.08] cursor-pointer transition-colors whitespace-nowrap"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-3 py-2 md:px-4 md:py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-pink-600 text-white text-xs md:text-sm font-medium hover:from-violet-500 hover:to-pink-500 cursor-pointer transition-all whitespace-nowrap"
          >
            시간 변경
          </button>
        </div>
      </div>
    </div>
  );
}